/**
 * AGC: Suggestions List Component
 * AGC: Displays all submitted suggestions
 */

import React, { useState, useEffect } from 'react'
import { X, MessageSquare, Clock } from 'lucide-react'
import { buildBackendUrl } from '../utils/backendUrl'

interface Comment {
  id: string
  text: string
  createdAt: number
}

interface Suggestion {
  id: string
  title: string
  category: string
  text: string
  createdAt: number
  updatedAt: number
  commentCount: number
  comments?: Comment[]
}

interface SuggestionsListProps {
  onClose: () => void
  onSuggestionClick: (suggestion: Suggestion) => void
}

export const SuggestionsList: React.FC<SuggestionsListProps> = ({ onClose, onSuggestionClick }) => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchSuggestions()
  }, [])

  const fetchSuggestions = async () => {
    try {
      setIsLoading(true)
      setError(null)

      const response = await fetch(buildBackendUrl('api/suggestions'))

      if (!response.ok) {
        throw new Error('Failed to fetch suggestions')
      }

      const data = await response.json()
      setSuggestions(data.suggestions || [])
    } catch (err: any) {
      setError(err.message || 'Failed to load suggestions')
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (timestamp: number) => {
    try {
      return new Date(timestamp).toLocaleString()
    } catch {
      return 'Unknown date'
    }
  }

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'Token': 'bg-yellow-500/20 text-yellow-400',
      'Game Mechanics': 'bg-blue-500/20 text-blue-400',
      'Blockchain': 'bg-purple-500/20 text-purple-400',
      'UI/UX': 'bg-pink-500/20 text-pink-400',
      'Buffs & NFTs': 'bg-green-500/20 text-green-400',
      'Battle System': 'bg-red-500/20 text-red-400',
      'Other': 'bg-gray-500/20 text-gray-400'
    }
    return colors[category] || colors['Other']
  }

  return (
    <div className="bg-slate-800 rounded-xl p-6 max-w-4xl w-full mx-auto max-h-[90vh] flex flex-col">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white">💡 All Suggestions</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors"
          aria-label="Close"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-400">Loading suggestions...</span>
        </div>
      ) : error ? (
        <div className="p-4 bg-red-900/50 border border-red-500 rounded-lg text-red-200">
          {error}
        </div>
      ) : suggestions.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p className="text-lg">No suggestions yet</p>
          <p className="text-sm mt-2">Be the first to share your ideas!</p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto space-y-3">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.id}
              onClick={() => onSuggestionClick(suggestion)}
              className="w-full text-left p-4 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors border border-slate-600"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-white truncate">{suggestion.title}</h3>
                    <span className={`px-2 py-1 rounded text-xs font-semibold whitespace-nowrap ${getCategoryColor(suggestion.category)}`}>
                      {suggestion.category}
                    </span>
                  </div>
                  <p className="text-sm text-gray-300 line-clamp-2 mb-2">{suggestion.text}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-400">
                    <div className="flex items-center gap-1">
                      <MessageSquare className="w-4 h-4" />
                      <span>{suggestion.commentCount || 0} comments</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{formatDate(suggestion.createdAt)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

